import { useState } from 'react';
import { IonHeader, IonToolbar, IonIcon } from '@ionic/react';
import { menuController } from '@ionic/core/components';
import { locationOutline, locateOutline } from 'ionicons/icons';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar } from './Avatar';
import { ActivityBell } from '../activity/ActivityBell';
import { LocaleSelectorModal } from '../location/LocaleSelectorModal';
import { useLocationStore } from '../../store/useLocationStore';
import { useThemeStore } from '../../store/useThemeStore';
import { useDisplayProfile } from '../../hooks/useDisplayProfile';
import { PROFILE_MENU_ID } from './ProfileMenu';
import { GUEST_MENU_ID } from './GuestMenu';
import { useInAppHistoryStore } from '../../store/useInAppHistoryStore';
import { useActivitySheetStore } from '../../store/useActivitySheetStore';

interface TopBarProps {
  /** Show the back button on the left of the toolbar. Defaults to true. Set
   *  to false on screens where backward navigation does not make sense
   *  (e.g. the home feed, post-payment confirmation, post-form-save destinations). */
  showBack?: boolean;
  /** Page name for the sticky title band under the toolbar. The shared toolbar
   *  carries no page identity (logo, location pill, bell, avatar), so a user
   *  who lands on a sidebar destination from the ProfileMenu drawer has
   *  nothing on screen naming where they are. Pass the page's `menu.*` label
   *  to render that name; omit it on pages that don't need one (the home feed,
   *  detail pages whose content is self-identifying).
   *
   *  The band lives inside IonHeader as a second toolbar rather than in the
   *  scrolling content, so it stays pinned while the page scrolls. IonContent
   *  sizes itself below the whole header, so pages need no layout changes and
   *  PullToRefresh is unaffected. */
  title?: string;
  /** Collapse the title band (see `useCollapsingTitle`). The band animates
   *  out on scroll-down and back in on scroll-up; the owning page drives this
   *  because the scroll events fire on its IonContent, not here. Ignored when
   *  no `title` is set. */
  titleCollapsed?: boolean;
  /** Optional "reset" handler for the logo. On pages that already live at "/"
   *  (the home feed) tapping the logo can't navigate anywhere, so the owning
   *  page passes this to re-run its own refresh (scroll to top + refetch)
   *  instead of a dead no-op. Ignored on any route other than "/". */
  onLogoReset?: () => void;
}

const MOBILE_ICON_VARIANT: 'default' | 'svg' = 'default';

export const TopBar: React.FC<TopBarProps> = ({ showBack = true, title, titleCollapsed = false, onLogoReset }) => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const { pathname } = useLocation();
  const canGoBack = useInAppHistoryStore((s) => s.depth > 0);
  // Use the lightweight optimistic-paint snapshot so the avatar shows on
  // first render for returning users (rather than flashing the Sign In
  // button while whoami's getProfileById runs). The snapshot is the
  // localStorage-cached subset of profile; resolves to live activeProfile
  // post-whoami. See `useDisplayProfile` for the resolution order.
  const displayProfile = useDisplayProfile();
  const location = useLocationStore((s) => s.location);
  const locale = useLocationStore((s) => s.locale);
  const locationSource = useLocationStore((s) => s.locationSource);
  const isDark = useThemeStore((s) => s.isDark);
  const [locationOpen, setLocationOpen] = useState(false);
  const openActivitySheet = useActivitySheetStore((s) => s.open);

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/');
    }
  };

  // Prefer the resolved locale's name (set on dish locales like "Vail Valley");
  // fall back to city/state, then postalCode, then empty.
  const locationLabel = locale?.name
    || (location?.city && location?.stateProvince
        ? `${location.city}, ${location.stateProvince}`
        : location?.postalCode || '');

  // Crosshair = device location (precise coords, `locationSource === 'device'`);
  // plain pin = a typed ZIP or an auto-resolved/login locale. This is the SAME
  // flag that unlocks dish-card distances, so the crosshair and distances are
  // always in lockstep. See the source line in LocaleSelectorModal.
  const locationIcon = locationSource === 'device' ? locateOutline : locationOutline;

  return (
    <IonHeader data-testid="top-bar">
      <IonToolbar>
        <div className="flex h-16 w-full items-center gap-3 px-4 sm:px-6">
          {showBack && canGoBack && (
            <button
              type="button"
              onClick={handleBack}
              aria-label={t('ui.goBack')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted active:opacity-80"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          )}
          <Link
            to="/"
            className="flex shrink-0 items-center"
            onClick={(e) => {
              // Already on the home feed → the route can't change, so hand off
              // to the page's reset (scroll to top + refresh) rather than let
              // the Link no-op.
              if (pathname === '/' && onLogoReset) {
                e.preventDefault();
                onLogoReset();
              }
            }}
          >
            <img
              src="/images/HomePlatedTM.svg"
              alt="HomePlated"
              width={140}
              height={28}
              className="hp-topbar-logo hidden sm:block"
            />
            {/* h-10 w-10 (rem) rather than the fixed 40px so the logo scales in
                lockstep with the other top-bar controls at larger text sizes.
                Icon variant is picked from useThemeStore.isDark rather than a
                CSS visibility gate — first attempt used chained Tailwind
                variants (`dark:max-sm:block`) which leaked in light mode. The
                store is already reactive to user override + system-pref
                changes, so a re-render on flip is fine and unambiguous. */}
            <img
              src={
                isDark
                  ? '/icons/web-app-icon-220x220-dark.png'
                  : '/icons/web-app-icon-220x220.png'
              }
              alt="HomePlated"
              width={40}
              height={40}
              className="hp-topbar-icon block sm:hidden h-10 w-10"
            />
          </Link>

          {/* The location pill lives in a flex-1 wrapper that absorbs the
              leftover space between the logo and the right-hand controls. That
              wrapper is a container-query context (see `.hp-loc-wrap` in
              global.scss): when the leftover gets too tight to show a readable
              label — long place name + large text + a back arrow all competing
              for width — the label is dropped and the pill collapses to just
              the (still-tappable) pin icon, instead of an unreadable stub. */}
          {/* Location switcher — always tappable so users can set/change
              location from any screen, not just the home feed. With a known
              location it shows the place label; with none it collapses to an
              icon-only button (the label span is simply omitted). */}
          <div className="hp-loc-wrap flex min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setLocationOpen(true)}
              aria-label={locationLabel || t('ui.setLocation')}
              className="hp-loc-pill flex h-10 min-w-0 max-w-[200px] items-center gap-1.5 rounded-full px-3 text-sm text-muted-foreground transition-colors hover:text-brand-primary active:opacity-80"
            >
              <IonIcon icon={locationIcon} className="hp-loc-icon shrink-0 text-base" />
              {locationLabel && <span data-testid="locale-name" className="hp-loc-label truncate">{locationLabel}</span>}
            </button>
          </div>

          {displayProfile ? (
            <div className="flex shrink-0 items-center gap-1">
              <ActivityBell onClick={openActivitySheet} />
              {/* Avatar opens the profile navigation drawer (ProfileMenu)
                  rather than routing straight to /profile — the drawer is the
                  new hub for the broken-out profile section pages. */}
              <button
                type="button"
                className="flex"
                data-testid="user-avatar"
                aria-label={t('ui.openMenu')}
                onClick={() => menuController.open(PROFILE_MENU_ID)}
              >
                <Avatar
                  src={displayProfile.avatarUrl}
                  name={displayProfile.displayName}
                  size="md"
                  className="!h-10 !w-10"
                />
              </button>
            </div>
          ) : (
            // Hamburger → guest drawer (GuestMenu): Sign In + language switcher.
            // Mirrors the authed avatar button on the right; opens the same-side
            // IonMenu addressed by GUEST_MENU_ID.
            <button
              type="button"
              data-testid="guest-menu-button"
              aria-label={t('ui.openMenu')}
              onClick={() => menuController.open(GUEST_MENU_ID)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted active:opacity-80"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}
        </div>
      </IonToolbar>

      {/* Page-title band. A second IonToolbar (the standard Ionic way to stack
          header rows), so it sits outside the scroll container and is shown or
          hidden by `titleCollapsed` rather than by scrolling out of view.
          Sized with min-height + padding rather than a fixed height so the row
          grows with the user's text size instead of clipping the title. */}
      {title && (
        <IonToolbar className="hp-title-bar">
          {/* Three nested elements, each with one job: `__clip` animates and
              hides the overflow, `__row` carries the padding and the hairline
              (so both are clipped away with it rather than lingering as a
              stray 1px line), and the h1 is the content. Collapsing the row
              directly would leave its border behind. */}
          <div
            className={`hp-title-bar__clip${titleCollapsed ? ' is-collapsed' : ''}`}
            aria-hidden={titleCollapsed}
          >
            <div className="hp-title-bar__row flex w-full items-center px-4 py-2.5 sm:px-6">
              <h1 className="truncate font-heading text-lg font-semibold text-foreground">
                {title}
              </h1>
            </div>
          </div>
        </IonToolbar>
      )}

      <LocaleSelectorModal isOpen={locationOpen} onClose={() => setLocationOpen(false)} />
    </IonHeader>
  );
};
